import i18n from "src/i18n/config";
import { z } from "zod";

import { showErrorToast } from "@shared/toasts/Toasts";

import { ProvisioningSettings } from "../types";

const appPermissionSchema = z.object({
    package: z.string(),
    permission: z.string(),
    action: z.enum(["Grant", "Revoke"]),
    comment: z.string().nullable(),
});
const appPreferenceSchema = z.object({
    package: z.string(),
    category: z.string(),
    type: z.enum(["Bool", "Float", "Int", "Long", "Set", "String"]),
    key: z.string(),
    value: z.string(),
    comment: z.string().nullable(),
});
const authorizedSshKeySchema = z.object({
    name: z.string(),
    key: z.string(),
    comment: z.string().nullable(),
});
const fDroidRepoSchema = z.object({
    name: z.string(),
    title: z.string(),
    url: z
        .string()
        .regex(/^https:\/\/apps\.emteria\.com\/fdroid\/[^/]+\/repo$/, i18n.t("common:validation.invalidFdroidRepoUrl")),
    description: z.string(),
});
const hostedApkSchema = z.object({
    name: z.string(),
    url: z.string(),
    comment: z.string().nullable(),
});
const systemPropertySchema = z.object({
    property: z.string(),
    value: z.string(),
    comment: z.string().nullable(),
});
const systemSettingSchema = z.object({
    section: z.enum(["Global", "Secure", "System"]),
    type: z.enum(["Bool", "Float", "Int", "Long", "Set", "String"]),
    key: z.string(),
    value: z.string(),
    comment: z.string().nullable(),
});
const wifiNetworkSchema = z.object({
    ssid: z.string(),
    password: z.string().optional(),
    description: z.string().optional(),
    keyManagement: z.array(z.string()),
    hidden: z.boolean(),
});

const exportProvisioningSettingsSchema = z.object({
    appPermissions: z.array(appPermissionSchema).optional(),
    appPreferences: z.array(appPreferenceSchema).optional(),
    authorizedSshKeys: z.array(authorizedSshKeySchema).optional(),
    automaticSetup: z.boolean(),
    acceptedEula: z.boolean(),
    bootAnimationUrl: z.string().nullable().optional(),
    deviceManager: z.string().nullable().optional(),
    disabledComponents: z.array(z.string()).optional(),
    enableBluetooth: z.boolean(),
    enableWifi: z.boolean(),
    enableAdbServer: z.boolean(),
    enableSshServer: z.boolean(),
    enableSftpServer: z.boolean(),
    enableVncServer: z.boolean(),
    enableMdmClient: z.boolean(),
    fDroidRepos: z.array(fDroidRepoSchema).optional(),
    fontScaling: z.coerce.number(),
    hostedApks: z.array(hostedApkSchema).optional(),
    orientationLock: z.boolean(),
    screenHeight: z.coerce.number(),
    screenWidth: z.coerce.number(),
    screenFrequency: z.coerce.number(),
    screenDensity: z.coerce.number(),
    screenRotation: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]),
    systemProperties: z.array(systemPropertySchema).optional(),
    systemSettings: z.array(systemSettingSchema).optional(),
    universalLicenses: z.array(z.string()).optional(),
    wallpaperUrl: z.string().nullable().optional(),
    ethernet: z
        .object({
            enabled: z.boolean(),
        })
        .optional(),
    gsm: z
        .object({
            enabled: z.boolean(),
        })
        .optional(),
    wifi: z.array(wifiNetworkSchema).optional(),
});

type ExportProvisioningSettings = z.infer<typeof exportProvisioningSettingsSchema>;

const normalizeSettings = (data: ProvisioningSettings): ExportProvisioningSettings => {
    const wifiNetworks = data.internetConfig?.find((item) => item.type === "wifi")?.networks || [];

    return {
        ...data,
        fDroidRepos: data.fDroidRepos.map((item) => ({
            name: item.name,
            title: item.title,
            description: item.description,
            url: `https://apps.emteria.com/fdroid/${item.name}/repo`,
        })),
        ethernet: {
            enabled: !!data.internetConfig?.find((item) => item.type === "ethernet"),
        },
        gsm: {
            enabled: !!data.internetConfig?.find((item) => item.type === "modem"),
        },
        wifi: wifiNetworks.map((item) => ({
            ssid: item.ssid,
            password: item.password,
            description: item.description,
            keyManagement: ["wpa_psk"],
            hidden: false,
        })),
    };
};

export const provisioningSettingsConverter = async (
    data: ProvisioningSettings,
): Promise<ExportProvisioningSettings | undefined> => {
    const provisioning = normalizeSettings(data);
    try {
        return exportProvisioningSettingsSchema.parse(provisioning);
    } catch (error) {
        if (error instanceof Error) {
            showErrorToast(error.message);
        }
    }
};
